import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Divider,
  Collapse,
} from "@mui/material";
import { Icon } from "@iconify/react";
import {
  useValidateImportDataMutation,
  useExecuteImportMutation,
} from "../../../features/import/importApiSlice";

function DryRunValidation({ cleanedData, onValidated, onExecute }) {
  const [validate, { isLoading: isValidating }] =
    useValidateImportDataMutation();
  const [executeImport, { isLoading: isExecuting }] =
    useExecuteImportMutation();
  const [validationResults, setValidationResults] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const handleValidate = useCallback(async () => {
    try {
      if (!cleanedData || cleanedData.length === 0) {
        console.error("No data to validate");
        setValidationResults({
          canProceed: false,
          summary: {
            sites: 0,
            members: 0,
            contracts: 0,
            events: 0,
            bookings: 0,
            siteManagers: 0,
          },
          issues: [
            {
              message: "Keine Daten vorhanden",
              details: "Es wurden keine Daten zum Validieren gefunden",
            },
          ],
        });
        return;
      }

      const payload = cleanedData.map(({ sourceIndex, ...rest }) => rest);
      const result = await validate({ data: payload }).unwrap();
      const contractSet = new Set();
      let noIbanContracts = 0;
      cleanedData.forEach((row) => {
        const iban = row?.iban ? String(row.iban).replace(/\s+/g, "") : "";
        if (iban) {
          contractSet.add(iban);
          return;
        }
        if (row?.owner || row?.bic) {
          noIbanContracts += 1;
        }
      });

      const normalizedResult = {
        ...result,
        summary: {
          sites: result?.summary?.sites || 0,
          members: result?.summary?.members || 0,
          contracts:
            result?.summary?.contracts || contractSet.size + noIbanContracts,
          events: result?.summary?.events || 0,
          bookings: result?.summary?.bookings || 0,
          siteManagers: result?.summary?.siteManagers || 0,
        },
      };
      setValidationResults(normalizedResult);
      onValidated(normalizedResult);
    } catch (err) {
      console.error("Validation error:", err);
      setValidationResults({
        canProceed: false,
        summary: {
          sites: 0,
          members: 0,
          contracts: 0,
          events: 0,
          bookings: 0,
          siteManagers: 0,
        },
        issues: [
          {
            message: "Validierungsfehler",
            details: err?.data?.message || err.message || "Unbekannter Fehler",
          },
        ],
      });
    }
  }, [cleanedData, onValidated, validate]);

  const handleExecuteImport = useCallback(async () => {
    try {
      if (!cleanedData || cleanedData.length === 0) {
        console.error("No data to import");
        return;
      }

      const payload = cleanedData.map(({ sourceIndex, ...rest }) => rest);
      const result = await executeImport({ data: payload }).unwrap();
      console.log("Import result:", result);

      // Ensure we pass the result in the expected format
      const contractSet = new Set();
      let noIbanContracts = 0;
      cleanedData.forEach((row) => {
        const iban = row?.iban ? String(row.iban).replace(/\s+/g, "") : "";
        if (iban) {
          contractSet.add(iban);
          return;
        }
        if (row?.owner || row?.bic) {
          noIbanContracts += 1;
        }
      });

      const contractCount =
        result?.summary?.contracts || contractSet.size + noIbanContracts;

      const importResults = {
        success: typeof result.success === "boolean" ? result.success : true,
        summary: {
          ...(result.summary || {}),
          contracts: contractCount,
        },
        errors: result.errors || [],
      };

      onExecute(importResults);
    } catch (err) {
      console.error("Import error:", err);

      // Pass error as result
      const errorResults = {
        success: false,
        summary: {
          sites: 0,
          members: 0,
          contracts: 0,
          events: 0,
          bookings: 0,
          siteManagers: 0,
        },
        errors: [
          {
            message: "Import fehlgeschlagen",
            details: err?.data?.message || err.message || "Unbekannter Fehler",
          },
        ],
      };

      onExecute(errorResults);
    }
  }, [cleanedData, executeImport, onExecute]);

  useEffect(() => {
    // Auto-run validation on mount or when cleaned data changes
    handleValidate();
  }, [handleValidate]);

  if (isValidating) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <CircularProgress size={64} />
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Trockenlauf wird durchgeführt...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ihre Daten werden validiert. Dies kann einen Moment dauern.
        </Typography>
      </Box>
    );
  }

  if (isExecuting) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <CircularProgress size={64} />
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Import wird ausgeführt...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ihre Daten werden in die Datenbank importiert. Bitte warten Sie.
        </Typography>
      </Box>
    );
  }

  if (!validationResults) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Alert severity="error">
          <Typography variant="body2">
            Validierung fehlgeschlagen. Bitte versuchen Sie es erneut.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const { summary, canProceed, issues, warnings } = validationResults;

  return (
    <Box>
      <Typography
        variant="h5"
        gutterBottom
        fontWeight="bold"
        textAlign="center"
      >
        Validierung abgeschlossen
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        textAlign="center"
        paragraph
      >
        {canProceed
          ? "Ihre Daten sind bereit für den Import!"
          : "Es wurden Probleme gefunden, die behoben werden müssen"}
      </Typography>

      {canProceed ? (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            ✓ Alle Validierungen erfolgreich
          </Typography>
          <Typography variant="caption">
            Ihre Daten erfüllen alle Anforderungen und können importiert werden.
          </Typography>
        </Alert>
      ) : (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="bold">
            ✗ Validierung fehlgeschlagen
          </Typography>
          <Typography variant="caption">
            Es wurden {issues?.length || 0} Probleme gefunden. Bitte beheben Sie
            diese vor dem Import.
          </Typography>
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Import-Zusammenfassung
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <Icon icon="mdi:map-marker" width={24} color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={`${summary?.sites || 0} Standorte`}
              secondary="werden erstellt oder aktualisiert"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Icon icon="mdi:account-group" width={24} color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={`${summary?.members || 0} Mitglieder`}
              secondary="werden importiert"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <Icon icon="mdi:file-document" width={24} color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={`${summary?.contracts || 0} Verträge`}
              secondary="werden erstellt"
            />
          </ListItem>
          {summary?.events > 0 && (
            <ListItem>
              <ListItemIcon>
                <Icon icon="mdi:calendar" width={24} color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={`${summary?.events} Veranstaltungen`}
                secondary="werden importiert"
              />
            </ListItem>
          )}
          {summary?.bookings > 0 && (
            <ListItem>
              <ListItemIcon>
                <Icon icon="mdi:cash-register" width={24} color="primary" />
              </ListItemIcon>
              <ListItemText
                primary={`${summary?.bookings} Buchungen`}
                secondary="werden importiert"
              />
            </ListItem>
          )}
        </List>
      </Paper>

      {warnings && warnings.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: "#fff4e5" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="h6" color="warning.main">
              Warnungen ({warnings.length})
            </Typography>
            <Button
              size="small"
              onClick={() => setShowDetails(!showDetails)}
              endIcon={
                <Icon
                  icon={showDetails ? "mdi:chevron-up" : "mdi:chevron-down"}
                />
              }
            >
              {showDetails ? "Ausblenden" : "Details anzeigen"}
            </Button>
          </Box>
          <Collapse in={showDetails}>
            <Divider sx={{ my: 1 }} />
            <List dense>
              {warnings.map((warning, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Icon
                      icon="mdi:alert"
                      width={20}
                      style={{ color: "#ed6c02" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={warning.message}
                    secondary={warning.details}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Paper>
      )}

      {issues && issues.length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: "#fee" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="h6" color="error">
              Kritische Fehler ({issues.length})
            </Typography>
            <Button
              size="small"
              onClick={() => setShowDetails(!showDetails)}
              endIcon={
                <Icon
                  icon={showDetails ? "mdi:chevron-up" : "mdi:chevron-down"}
                />
              }
            >
              {showDetails ? "Ausblenden" : "Details anzeigen"}
            </Button>
          </Box>
          <Collapse in={showDetails}>
            <Divider sx={{ my: 1 }} />
            <List dense>
              {issues.map((issue, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Icon
                      icon="mdi:alert-circle"
                      width={20}
                      style={{ color: "#d32f2f" }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={issue.message}
                    secondary={issue.details}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Paper>
      )}

      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight="bold">
          Wichtiger Hinweis
        </Typography>
        <Typography variant="caption">
          Nach dem Import können die Daten nicht automatisch rückgängig gemacht
          werden. Stellen Sie sicher, dass alle Daten korrekt sind.
        </Typography>
      </Alert>

      <Box sx={{ mt: 4, display: "flex", gap: 2, justifyContent: "center" }}>
        <Button
          variant="outlined"
          size="large"
          onClick={handleValidate}
          startIcon={<Icon icon="mdi:refresh" />}
        >
          Neu validieren
        </Button>
        <Button
          variant="contained"
          size="large"
          color="primary"
          onClick={handleExecuteImport}
          disabled={!canProceed}
          startIcon={<Icon icon="mdi:database-import" />}
        >
          Import jetzt starten
        </Button>
      </Box>
    </Box>
  );
}

DryRunValidation.propTypes = {
  cleanedData: PropTypes.array.isRequired,
  onValidated: PropTypes.func.isRequired,
  onExecute: PropTypes.func.isRequired,
};

export default DryRunValidation;
